import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const USE_FIREBASE = process.env.REACT_APP_USE_FIREBASE === "true";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

let apiInstance;
let firebaseModules = {};

if (USE_FIREBASE) {
  // Dynamic imports will be resolved at build time
  const { createFirestoreApi } = require("../services/firestoreApi");
  const { auth: fbAuth } = require("../lib/firebase");
  const { signInWithEmailAndPassword, signOut, onAuthStateChanged } = require("firebase/auth");
  const { doc, getDoc } = require("firebase/firestore");
  const { db } = require("../lib/firebase");
  apiInstance = createFirestoreApi();
  firebaseModules = { fbAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged, doc, getDoc, db };
} else {
  apiInstance = axios.create({ baseURL: `${API_BASE}/api`, withCredentials: true });
  apiInstance.interceptors.request.use((config) => {
    const token = localStorage.getItem("agbill_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ============ FIREBASE AUTH ============
  const firebaseInit = useCallback(() => {
    if (!USE_FIREBASE) return;
    const { fbAuth, onAuthStateChanged, doc, getDoc, db } = firebaseModules;
    return onAuthStateChanged(fbAuth, async (fbUser) => {
      if (fbUser) {
        let profile = {};
        try {
          const userDoc = await getDoc(doc(db, "users", fbUser.uid));
          if (userDoc.exists()) profile = userDoc.data();
        } catch {}
        setUser({
          id: fbUser.uid, email: fbUser.email,
          name: profile.name || fbUser.displayName || "Admin",
          role: profile.role || "superadmin",
        });
      } else {
        setUser(false);
      }
      setLoading(false);
    });
  }, []);

  // ============ API AUTH ============
  const apiInit = useCallback(async () => {
    if (USE_FIREBASE) return;
    try {
      const { data } = await apiInstance.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
      localStorage.removeItem("agbill_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (USE_FIREBASE) {
      const unsub = firebaseInit();
      return () => unsub && unsub();
    } else {
      apiInit();
    }
  }, [firebaseInit, apiInit]);

  const login = async (email, password) => {
    if (USE_FIREBASE) {
      const { fbAuth, signInWithEmailAndPassword, doc, getDoc, db } = firebaseModules;
      const cred = await signInWithEmailAndPassword(fbAuth, email, password);
      let profile = {};
      try {
        const userDoc = await getDoc(doc(db, "users", cred.user.uid));
        if (userDoc.exists()) profile = userDoc.data();
      } catch {}
      const userData = {
        id: cred.user.uid, email: cred.user.email,
        name: profile.name || cred.user.displayName || "Admin",
        role: profile.role || "superadmin",
      };
      setUser(userData);
      return userData;
    } else {
      const { data } = await apiInstance.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("agbill_token", data.token);
      setUser(data);
      return data;
    }
  };

  const logout = async () => {
    if (USE_FIREBASE) {
      const { fbAuth, signOut } = firebaseModules;
      await signOut(fbAuth);
      setUser(false);
    } else {
      try { await apiInstance.post("/auth/logout"); } catch {}
      localStorage.removeItem("agbill_token");
      setUser(false);
    }
  };

  const checkAuth = useCallback(async () => {
    if (!USE_FIREBASE) await apiInit();
  }, [apiInit]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, api: apiInstance, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}

export { apiInstance as api };
